"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeroSection from "./components/CoconutGroveHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";

const neighborhood = "Coconut Grove";

export default function CoconutGrovePage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const coconutGroveFaqs = [
    { question: t("hood.faq1Q"), answer: t("hood.faq1A") },
    { question: t("hood.faq2Q"), answer: t("hood.faq2A") },
    { question: t("hood.faq3Q"), answer: t("hood.faq3A") },
    { question: t("hood.faq4Q"), answer: t("hood.faq4A") },
    { question: t("coconutGrove.faq5Q"), answer: t("coconutGrove.faq5A") },
  ];

  useEffect(() => {
    document.title =
      currentLang === "es"
        ? "Reparaci\u00F3n de Ventanas Coconut Grove \u2014 Bayfront Miami"
        : "Coconut Grove Window Repair \u2014 Bayfront & Humid Climate";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      currentLang === "es"
        ? "Especialistas en reparaci\u00F3n de ventanas en Coconut Grove. Experiencia en clima h\u00FAmido bayfront. Mantenimiento de propiedades con \u00E1rboles y servicio de limpieza de rieles Miami-Dade."
        : "Coconut Grove window repair specialists. Bayfront humid climate expertise. Tree-shaded property maintenance and track cleaning service Miami-Dade.";

    const cleanupSeo = injectMetaTags({
      title: document.title,
      description: metaDesc.content,
      url: "https://impact-window-repair-miami.com/impact-windows-coconut-grove/",
      image: "https://impact-window-repair-miami.com/og/og-coconut-grove.jpg",
      imageAlt:
        currentLang === "es"
          ? "Reparaci\u00F3n de ventanas Coconut Grove Miami"
          : "Window repair Coconut Grove Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "\u00C1reas de Servicio" : "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: neighborhood, item: "https://impact-window-repair-miami.com/impact-windows-coconut-grove/" },
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Window Repair",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: {
            "@type": "City",
            name: "Coconut Grove, Miami-Dade, FL",
          },
          serviceType: "Window Repair",
          description:
            currentLang === "es"
              ? "Reparaci\u00F3n de herrajes, vidrios y mantenimiento en Coconut Grove."
              : "Hardware repair, glass replacement, and maintenance in Coconut Grove.",
        },
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: coconutGroveFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      const existingFaq = document.getElementById("faq-schema");
      if (existingFaq) document.head.removeChild(existingFaq);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection
        h1={t("coconutGrove.h1")}
        subhead={t("coconutGrove.subhead")}
        zips={["33133", "33134", "33129"]}
      />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACI\u00D3N EN COCONUT GROVE" : "COCONUT GROVE WINDOW REPAIR"}
        imageSrc="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/1357dca9-e496-4c9b-aa80-50afa22b0119_windows-hardware-miami.jpg?v=a49edce5c95b8c245efa1f0cad36005a"
        imageAlt={currentLang === "es" ? "Especialista en reparaci\u00F3n de ventanas Coconut Grove Miami" : "Coconut Grove Miami window repair specialist servicing residential property"}
        heading={currentLang === "es" ? "Reparaci\u00F3n de Ventanas en Coconut Grove" : "Coconut Grove Window Repair and Maintenance"}
        lead={t("coconutGrove.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Limpieza de rieles y servicio de herrajes",
                "Reparaci\u00F3n de puertas corredizas para patios bayfront",
                "Burletes para sellado en clima h\u00FAmido",
              ]
            : [
                "Track cleaning and hardware service",
                "Sliding door repair for bayfront patios",
                "Weatherstripping for humid climate sealing",
              ]
        }
        closing={
          currentLang === "es"
            ? "Las casas de Coconut Grove se benefician de mantenimiento regular para prevenir fallas de herrajes por humedad."
            : "Coconut Grove homes benefit from regular maintenance to prevent humidity-related hardware failure."
        }
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection heading={t("hood.commonFaqH2")} items={coconutGroveFaqs} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: currentLang === "es" ? "Brickell" : "Brickell", slug: "brickell" },
        { name: currentLang === "es" ? "Coral Gables" : "Coral Gables", slug: "coral-gables" },
        { name: currentLang === "es" ? "Key Biscayne" : "Key Biscayne", slug: "key-biscayne" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}